<template>
  <div class="workspace--list"
       v-if="workspace" :class="{ 'just-avatar': justAvatar }"
  >
    <AvatarWidget
        :image-key="workspace.avatar"
        :social-avatar="workspace.name"
        :image-size="imageSize"
        :full-name-avatar="workspace.textAvatar"
        :alt="workspace.name"
        :title="workspace.name"
    />

    <div v-if="publishTooltip" class="publish-menu-tooltip">
      {{ workspace.company || workspace.name }}
    </div>
    <div class="c-name" v-if="!justAvatar">
      <span>{{ workspace.company || workspace.name }}</span>
      <span>{{ workspace.url }}</span>
    </div>
    <div class="show-arrow" v-if="showArrow || (!showArrow && workspace.isActive)" :class="arrowClasses()">
      <LucideIcon v-if="!showArrow && workspace.isActive" icon="CircleCheck"/>
      <LucideIcon v-if="showArrow" icon="ChevronRight" class="arrow-right"/>
    </div>
  </div>
</template>

<script lang="ts">
import {defineComponent, type PropType} from 'vue';
import {WorkspaceNs} from '@shared/types';
import AvatarWidget from "@shared/components/widgets/profile/avatar.widget.vue";

export default defineComponent({
  name: 'WorkspaceCardSection',
  components: {AvatarWidget},
  props: {
    workspace: {
      type: Object as PropType<WorkspaceNs.Workspace>,
      required: true
    },
    showArrow: {
      type: Boolean,
      default: false
    },
    justAvatar: {
      type: Boolean,
      default: false
    },
    publishTooltip: {
      type: Boolean,
      default: false // Default is set to false
    },
    imageSize: {
      type: String,
      default: 'imageSM'
    }
  },
  computed: {
    imageUrl(): string {
      if (this.workspace.avatar) {
        return `${this.workspace.avatar}/imageSM`;
      }
      return '';
    }
  },
  data() {
    return {
      /** 0: NotExists | 1:Loading | 2:Loaded */
      imageStatus: 0
    };
  },
  beforeMount() {
    if (this.workspace.avatar) {
      this.imageStatus = 2;
    }
  },
  methods: {
    onImageError() {
      this.imageStatus = 0;
    },
    arrowClasses(): { [key: string]: boolean } {
      return {
        'c-arrow': true,
        check: this.showArrow
      };
    }
  }
});

</script>

<style lang="scss" scoped>
.workspace--list {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  position: relative;
  gap: var(--space-12);

  &:hover {
    .publish-menu-tooltip {
      display: flex;
    }

    .c-border {
      background-color: var(--border-neutral-dark);
    }

    .c-image {
      background-color: var(--light-100);
    }
  }

  .c-name {
    display: flex;
    width: 100%;
    flex-grow: 1;
    text-align: left;
    overflow: hidden;
    outline: none;
    flex-direction: column;
    justify-content: center;
    align-items: flex-start;
    z-index: 1;
    font-size: var(--fs-14);
    line-height: 20px;

    span:first-child {
      font-size: var(--fs-14);
      font-weight: 500;
      text-align: left;
      display: inline-block;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      width: 95%;
      letter-spacing: -0.6px;
    }

    span:nth-of-type(2) {
      font-size: var(--fs-12);
      color: var(--text-neutral);
      margin-top: var(--space-negative-2);
      font-weight: 400;
      text-align: left;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      display: inline-block;
      width: 95%;
      letter-spacing: -0.6px;
    }
  }

  .c-arrow {
    margin-left: auto;
    margin-right: 5px;
    display: flex;
    justify-content: center;
    align-items: center;
    color: var(--text-blue);

    .arrow-right {
      color: var(--text-neutral);
    }

    &.check {
      &:before {
        content: '';
        background-repeat: no-repeat;
        display: block;
        width: 15px;
        height: 15px;
      }
    }
  }
}

.workspace--list.just-avatar {
  display: flex;
  position: absolute;
  top: 30px;
  left: 27px;
  width: auto;

  .avatar--border {
    mask-image: url("data:image/svg+xml,%3Csvg clip-rule='evenodd' fill-rule='evenodd' viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='m18.947 18.947c-13.32 13.32-17.947 42.57-17.947 109.053s4.626 95.732 17.947 109.053c13.32 13.32 42.57 17.947 109.053 17.947s95.732-4.626 109.053-17.947c13.32-13.32 17.947-42.57 17.947-109.053s-4.626-95.732-17.947-109.053c-13.32-13.32-42.57-17.947-109.053-17.947s-95.732 4.626-109.053 17.947z' fill-rule='nonzero' stroke='%23000' transform='matrix(.992126 0 0 .992126 1.00787 1.00787)'/%3E%3C/svg%3E");
    background-color: var(--border-neutral-dark);
    width: 22px;
    height: 22px;
    min-width: 22px;
    min-height: 22px;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-right: 0;

    &:hover {
      background-color: var(--border-neutral-darker);
    }
  }

  .avatar--image {
    min-width: 20px;
    min-height: 20px;
    max-width: 20px;
    max-height: 20px;
    background-color: var(--light-100);
    border-radius: $br-sm;
    display: flex;
    justify-content: center;
    align-items: center;
    color: var(--text-neutral);
    mask-image: url("data:image/svg+xml,%3Csvg clip-rule='evenodd' fill-rule='evenodd' viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='m18.947 18.947c-13.32 13.32-17.947 42.57-17.947 109.053s4.626 95.732 17.947 109.053c13.32 13.32 42.57 17.947 109.053 17.947s95.732-4.626 109.053-17.947c13.32-13.32 17.947-42.57 17.947-109.053s-4.626-95.732-17.947-109.053c-13.32-13.32-42.57-17.947-109.053-17.947s-95.732 4.626-109.053 17.947z' fill-rule='nonzero' stroke='%23000' transform='matrix(.992126 0 0 .992126 1.00787 1.00787)'/%3E%3C/svg%3E");
    mask-position: center center;
    mask-repeat: no-repeat;

    &:hover {
      background-color: var(--light-100);
    }

    img {
      width: 20px;
      height: 20px;
      border-radius: $br-sm;
    }


    span {
      font-size: var(--fs-12);
      line-height: 20px;
      font-weight: 600;
      position: relative;
    }
  }

  .avatar--name {
    display: none;
  }

  .show-arrow {
    display: none;
  }
}

@media (max-width: 968px) {
  .app-publish {
    .workspace--list {
      .avatar--border {
        width: 34px;
        height: 34px;
        min-width: 34px;
        min-height: 34px;
      }

      .avatar--image {
        min-width: 32px;
        min-height: 32px;
        max-width: 32px;
        max-height: 32px;
      }
    }
  }
}

.publish-menu-tooltip {
  padding: 6px 10px;
  background-color: var(--text-dark);
  color: var(--light-100);
  border-radius: $br-sm;
  box-shadow: var(--shadow-lg);
  font-size: var(--fs-14);
  position: absolute;
  left: 66px;
  z-index: 2;
  font-weight: 600;
  white-space: nowrap;
  display: none;
}

@media (max-width: 968px) {
  .publish-menu-tooltip {
    display: none !important;
  }
}
</style>
